/**
 * Unified Product Addition from URL
 * Centralizes all logic for adding products via URL parsing
 * Used by both Home.jsx and AggiungiProdotto.jsx
 */

import { fetchProductMetadata } from './productMetadata.js';
import { selectBestImageFromPreview } from './imageFormatFilter.js';
import { normalizeImageUrl } from './normalizeImageUrl.js';
import { isValidProductUrl, getAffiliateLink } from './affiliateUtils.js';
import { moderateText } from './contentModeration.js';
import { cacheImageToFirebase } from './cacheImage.js';

/**
 * Validates product title - filters anti-bot pages, generic titles, error pages
 * @param {string} title - Product title to validate
 * @returns {boolean} - True if title is INVALID
 */
export function isInvalidProductTitle(title) {
  if (!title || title.length < 5) return true;
  const titleLower = title.toLowerCase().trim();
  
  const invalidPatterns = [
    'access to this page', 'access denied', 'access has been denied',
    'page has been denied', 'permission denied', 'forbidden',
    'just a moment', 'please wait', 'checking your browser',
    'attention required', 'robot check', 'verify you are human',
    'service unavailable', 'page not found', 'error 404', 'error 403',
    'error 500', 'error 502', 'error 503', 'internal server error',
    'maintenance', 'coming soon', 'under construction',
    'captcha', 'blocked', 'cloudflare', 'security check',
    'negozio online', 'online store', 'official site', 'home page',
    'shop online', 'tienda online', 'boutique en ligne',
    'pardon our interruption', 'are you a human', 'bot detection',
    'one moment', 'we will be back soon', 'verify',
    'product gallery', 'impossibile trovare', 'page cannot be found',
    'reference #', 'pagina non trovata', 'page introuvable'
  ];
  
  if (invalidPatterns.some(p => titleLower.includes(p))) return true;
  
  const genericStoreTitles = [
    'amazon', 'ebay', 'aliexpress', 'wish', 'etsy', 'zalando', 'asos',
    'farfetch', 'mytheresa', 'ssense', 'matchesfashion', 'net-a-porter',
    'mr porter', 'luisaviaroma', 'yoox', 'outnet', 'neiman marcus',
    'nordstrom', 'saks fifth', 'bergdorf', 'barneys', 'brownsfashion',
    'walmart', 'target', 'home', 'homepage'
  ];
  
  for (const site of genericStoreTitles) {
    const sitePattern = new RegExp(`^${site}(\\.\\w+)?\\s*[-|–—]?\\s*$`, "i");
    if (sitePattern.test(title) || titleLower === site) return true;
  }
  
  return false;
}

/**
 * Checks if an image URL is a placeholder/icon
 * @param {string} imageUrl - Image URL to check
 * @param {string} productUrl - Original product URL (for Amazon detection)
 * @returns {boolean} - True if placeholder
 */
export function isPlaceholderImage(imageUrl, productUrl) {
  if (!imageUrl) return true;
  
  const imageLower = imageUrl.toLowerCase();
  const isAmazonUrl = productUrl?.toLowerCase().includes('amazon.');
  
  return (
    imageLower.includes('/icon') ||
    imageLower.includes('placeholder') ||
    imageLower.includes('filetype') ||
    imageLower.includes('file-type') ||
    imageLower.includes('file_type') ||
    imageLower.includes('mime-type') ||
    imageLower.includes('mimetype') ||
    imageLower.includes('/assets/images/icon') ||
    imageLower.includes('default-image') ||
    imageLower.includes('no-image') ||
    (imageLower.includes('linkpreview') && (imageLower.includes('icon') || imageLower.includes('placeholder'))) ||
    (imageUrl.includes('.svg') && (imageLower.includes('icon') || imageLower.includes('placeholder'))) ||
    (isAmazonUrl && !imageLower.includes('ssl-images-amazon') && !imageLower.includes('media-amazon') && !imageLower.includes('images-amazon'))
  );
}

/**
 * Process and cache an image URL to Firebase Storage
 * @param {string} imageUrl - Image URL to process
 * @param {string} productUrl - Original product URL
 * @returns {Promise<{url: string|null, cached: boolean, error?: string}>}
 */
export async function processAndCacheImage(imageUrl, productUrl) {
  if (!imageUrl) {
    return { url: null, cached: false };
  }

  const normalizedImage = normalizeImageUrl(imageUrl, productUrl);
  if (!normalizedImage) {
    console.warn("⚠️ Image URL normalization failed:", imageUrl);
    return { url: null, cached: false, error: 'normalization_failed' };
  }

  const secureImage = normalizedImage.startsWith('http://') 
    ? normalizedImage.replace('http://', 'https://') 
    : normalizedImage;

  if (isPlaceholderImage(secureImage, productUrl)) {
    console.warn("⚠️ PLACEHOLDER DETECTED - Skipping:", secureImage.substring(0, 60));
    return { url: null, cached: false, error: 'placeholder' };
  }

  console.log("✅ Caching legitimate image to Firebase Storage...");
  
  try {
    const productId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const cacheResult = await cacheImageToFirebase(secureImage, productId);
    
    if (cacheResult.cached) {
      console.log("✅ Image cached successfully");
      return { url: cacheResult.url, cached: true };
    } else {
      console.warn("⚠️ Image caching failed, using original URL as fallback:", cacheResult.error);
      // Use the fallback URL from cacheResult or the secure image URL
      return { url: cacheResult.url || secureImage, cached: false, error: 'cache_failed' };
    }
  } catch (error) {
    console.error("❌ Image caching exception, using original URL:", error);
    // Fallback to the secure image URL instead of null
    return { url: secureImage, cached: false, error: error.message };
  }
}

/**
 * Main function: Process a product URL and extract metadata
 * @param {string} url - Product URL to process
 * @param {object} options - Processing options
 * @param {boolean} options.skipModeration - Skip content moderation
 * @param {boolean} options.skipImageCache - Skip image caching to Firebase
 * @returns {Promise<ProductResult>}
 */
export async function processProductUrl(url, options = {}) {
  const result = {
    success: false,
    title: null,
    image: null,
    affiliateUrl: null,
    originalUrl: url,
    error: null,
    errorKey: null,
    needsManualMode: false
  };

  if (!url || typeof url !== 'string') {
    result.error = 'No URL provided';
    result.errorKey = 'errors.linkRequired';
    return result;
  }

  const trimmedUrl = url.trim();
  console.log('🚀 [ProductAddition] Processing URL:', trimmedUrl.substring(0, 70));

  const urlValidation = isValidProductUrl(trimmedUrl);
  if (!urlValidation.valid) {
    console.log('❌ [ProductAddition] URL validation failed:', urlValidation.reasonKey);
    result.error = 'Invalid URL';
    result.errorKey = urlValidation.reasonKey;
    return result;
  }

  try {
    const metadata = await fetchProductMetadata(trimmedUrl);
    console.log('📦 [ProductAddition] Metadata received:', { 
      title: metadata.title?.substring(0, 50), 
      hasImage: !!metadata.image,
      provider: metadata.provider 
    });

    const title = metadata.title?.trim();
    const selectedImage = selectBestImageFromPreview(metadata);

    if (!title || isInvalidProductTitle(title)) {
      console.log('⚠️ [ProductAddition] Invalid/generic title detected:', title?.substring(0, 50));
      result.needsManualMode = true;
      result.error = 'Auto-load failed - manual entry required';
      result.errorKey = 'errors.autoLoadError';
      return result;
    }

    if (!options.skipModeration) {
      console.log('🔍 [ProductAddition] Moderating content...');
      const moderationResult = await moderateText(title, 'product');
      
      if (!moderationResult.allowed) {
        console.warn('🚫 [ProductAddition] Content blocked:', moderationResult.reason);
        result.error = moderationResult.reason || 'Content not allowed';
        result.errorKey = 'modals.bannedContent';
        return result;
      }
      console.log('✅ [ProductAddition] Content approved');
    }

    result.title = title;
    // getAffiliateLink expects an object with url property, not a string
    result.affiliateUrl = getAffiliateLink({ url: trimmedUrl });

    if (selectedImage && !options.skipImageCache) {
      console.log('📸 [ProductAddition] Processing image...');
      const imageResult = await processAndCacheImage(selectedImage, trimmedUrl);
      result.image = imageResult.url;
      
      if (!imageResult.cached && imageResult.error) {
        console.log('⚠️ [ProductAddition] Image processing failed, product will be saved without image');
      }
    } else if (selectedImage) {
      result.image = selectedImage;
    }

    result.success = true;
    console.log('✅ [ProductAddition] Success:', { 
      title: result.title?.substring(0, 40), 
      hasImage: !!result.image 
    });

    return result;

  } catch (error) {
    console.error('❌ [ProductAddition] Error:', error.message);
    result.needsManualMode = true;
    result.error = error.message;
    result.errorKey = 'errors.autoLoadError';
    return result;
  }
}
